import { useEffect, useMemo } from 'react'

import { QueryWorkingConditionsRolesAndLocationsDocument } from 'generated/resources/graphql'

import Selector from 'modules/core/components/Selector'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { Workplace } from 'modules/job_catalog/lib/salary'

interface WorkplaceSelectorProps {
  selectedWorkplaces: Workplace[]
  setSelectedWorkplaces: (value: Workplace[]) => void
  disabledWorkplaces: Workplace[]
  hasAllWorkplacesSalary: boolean
  onTotalWorkplacesChange?: (total: number) => void
}

export const WorkplaceSelector = ({
  selectedWorkplaces,
  setSelectedWorkplaces,
  disabledWorkplaces,
  hasAllWorkplacesSalary,
  onTotalWorkplacesChange,
}: WorkplaceSelectorProps) => {
  const { data, loading } = useQuery(QueryWorkingConditionsRolesAndLocationsDocument)

  const workplaces = useMemo(() => {
    if (!data?.locations?.locations) return []
    return data?.locations?.locations?.map((workplace) => ({
      id: workplace.id,
      name: workplace.name,
    }))
  }, [data])

  // Notify parent of total workplaces count
  useEffect(() => {
    onTotalWorkplacesChange?.(workplaces.length)
  }, [workplaces.length, onTotalWorkplacesChange])

  const isMandatory = useMemo(() => {
    return disabledWorkplaces.length === workplaces.length || hasAllWorkplacesSalary
  }, [disabledWorkplaces, workplaces, hasAllWorkplacesSalary])

  const selectedWorkplaceIds = useMemo(
    () => selectedWorkplaces.map((workplace) => workplace.id),
    [selectedWorkplaces]
  )

  const disabledWorkplaceIds = useMemo(
    () => disabledWorkplaces.map((workplace) => workplace.id),
    [disabledWorkplaces]
  )

  const handleChangeOptions = (selectedOptions: unknown) => {
    if (!Array.isArray(selectedOptions)) return
    setSelectedWorkplaces(workplaces.filter((workplace) => selectedOptions.includes(workplace.id)))
  }

  const workplaceOptions = [
    ...workplaces.map((workplace) => (
      <Selector.Option
        key={workplace.id}
        label={workplace.name}
        searchValue={workplace.name}
        value={workplace.id}
        isDisabled={disabledWorkplaceIds.includes(workplace.id)}
      >
        {workplace.name}
      </Selector.Option>
    )),
  ]

  if (loading) return
  return (
    <Selector.Popover
      label={i18n.t('job_catalog.nodes.salary_modal.workplace.title')}
      inputStyle="badge"
      isMultiSelect
      value={selectedWorkplaceIds}
      onChange={handleChangeOptions}
      placeholder={i18n.t('job_catalog.nodes.salary_modal.workplace.all')}
      blankLabel={i18n.t('job_catalog.nodes.salary_modal.workplace.blank')}
      mandatory={isMandatory}
    >
      {workplaceOptions}
    </Selector.Popover>
  )
}
